import { useState, FormEvent } from 'react';
import { useAuth } from '../../hooks/useAuth';
import './CustomerLoginPage.css';

type View = 'landing' | 'signin';

export default function CustomerLoginPage() {
  const { loginWithEmail, loginWithMfaCode, sendPasswordReset, loginError, mfaRequired } = useAuth();
  const [view, setView] = useState<View>('landing');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const handlePasswordReset = async () => {
    if (!email) {
      setResetError('Enter your email address above first.');
      return;
    }
    try {
      await sendPasswordReset(email);
      setResetSent(true);
      setResetError(null);
    } catch {
      setResetError('Could not send reset email. Check the address and try again.');
    }
  };

  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await loginWithEmail(email, password);
    setSubmitting(false);
  };

  const handleMfa = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await loginWithMfaCode(otp);
    setSubmitting(false);
  };

  return (
    <div className="customer-login-page">
      <div id="recaptcha-container" />

      <nav className="customer-login-navbar">
        <div className="customer-navbar-brand">
          <span>Pest<strong>Connect</strong> <em>LIVE</em></span>
        </div>
      </nav>

      <div className="customer-login-hero">
        <img src="/static/customer-login-page-image.jpg" alt="" className="customer-login-hero-img" />
        <div className="customer-hero-overlay">
          <h1 className="customer-hero-heading">Digital pest control management</h1>
          <p className="customer-hero-text">Take your pest management to new levels of efficiency with digital pest control solutions from Rentokil.</p>
        </div>
      </div>

      <div className="customer-login-body">
        <div className="customer-login-left">
          <div className="customer-login-center-content" key={view}>
            {view === 'landing' ? (
              <>
                <img src="/static/pestconnect.png" alt="PestConnect" className="customer-login-logo" />
                <button className="clp-sign-in-btn" onClick={() => setView('signin')}>
                  <strong>Customer login</strong>
                </button>
                {loginError && <div className="login-error">{loginError}</div>}
              </>
            ) : mfaRequired ? (
              <>
                <h2 className="clp-heading">Verification code</h2>
                <p className="clp-subtitle">Enter the code sent to your registered phone number.</p>
                <form onSubmit={handleMfa} className="clp-form">
                  <input className="clp-input" type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} required autoFocus />
                  {loginError && <p className="clp-error">{loginError}</p>}
                  <button type="submit" className="clp-submit-btn" disabled={submitting}><strong>{submitting ? 'Verifying...' : 'Verify'}</strong></button>
                </form>
              </>
            ) : (
              <>
                <h2 className="clp-heading">Sign in</h2>
                <form onSubmit={handleSignIn} className="clp-form">
                  <label className="clp-label">EMAIL ADDRESS</label>
                  <input className="clp-input" type="email" autoComplete="username" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
                  <label className="clp-label">PASSWORD</label>
                  <input className="clp-input" type="password" autoComplete="current-password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                  {resetSent ? (
                    <p className="clp-reset-confirm">If that address is registered you'll receive a reset email shortly.</p>
                  ) : (
                    <button type="button" className="clp-reset-link" onClick={handlePasswordReset}>SET/RESET PASSWORD</button>
                  )}
                  {resetError && <p className="clp-error">{resetError}</p>}
                  {loginError && <p className="clp-error">{loginError}</p>}
                  <button type="submit" className="clp-submit-btn" disabled={submitting}><strong>{submitting ? 'Signing in...' : 'Sign in'}</strong></button>
                </form>
                <button type="button" className="clp-back-btn" onClick={() => setView('landing')}>← Back</button>
              </>
            )}
          </div>

          <div className="customer-login-bottom-row">
            <nav className="customer-login-links">
              <a href="https://www.rentokil.com/company-information" className="customer-login-link" target="_blank" rel="noopener noreferrer">Company information</a>
              <a href="https://www.rentokil.com/legal" className="customer-login-link" target="_blank" rel="noopener noreferrer">Legal statement</a>
              <a href="https://www.rentokil-initial.com/site-services/cookie-and-privacy-policy/privacy-policy.aspx" className="customer-login-link" target="_blank" rel="noopener noreferrer">Privacy policy</a>
              <a href="https://www.rentokil-initial.com/site-services/cookie-and-privacy-policy/cookie-policy.aspx" className="customer-login-link" target="_blank" rel="noopener noreferrer">Cookie policy</a>
            </nav>
          </div>
        </div>

        <div className="customer-login-right">
          <img src="/static/login-img.png" alt="" className="customer-login-img" />
        </div>
      </div>
    </div>
  );
}
